// Linked tokens on the Roll20 map: a mount and its rider, aura art under a
// caster, a marker on a creature. A link is a FOLLOW, not a Roll20 group —
// every token stays its own token (clickable, bars editable, damage taken
// as usual) and when any member moves the others move by the same delta.
// The connector's token lane (lmc-tokens.js, 0.8.178) keeps the follow in
// the game tab's page world, so it dies with a Roll20 reload; this store
// keeps the table per campaign in the layout and re-pushes it.
import { newRequestId, onBridge, request } from "../bridge";
import type { Layout } from "./types";
import { AURAS } from "./auras";

/** off: dismounted — kept so the drop-back-on re-mount survives a Roll20 reload */
/** `kind` is what this token is to the base (see state/attachments.ts). Optional:
 *  links recorded before kinds existed carry none, and are read as a plain follow. */
export interface TokenRef { id: string; name: string; attached?: boolean; off?: boolean; kind?: string }
/** mount: a controlled mount (worded mounts / dismounts); off, a creature
 *  climbed on (climbs onto / off, thrown). Attached riders are effects and
 *  art that follow the base and never dismount. */
export interface TokenLink { base: TokenRef; riders: TokenRef[]; remount?: boolean; mount?: boolean }
export interface LaneEvent { seq: number; kind: "mount" | "dismount" | "thrown"; reason?: string; mount?: boolean; rider: TokenRef; base: TokenRef; at: number }
/** A token that is an effect, not a creature: a spell's emanation, a swarm
 *  the DM drew, a light. Never a base, never a rider that could climb off —
 *  it can only be attached to its caster. */
const EFFECT_WORDS = /\b(swarm|wall of|cloud|fog|sphere|web|darkness|silence|zone of|circle of|template|emanation|aura|guardians|\(light\)|light source|hazard|difficult terrain|pit|fire|smoke|storm)\b/i;
/**
 * Our own spell art or aura art, by NAME.
 *
 * This used to say "anything not on the objects layer", which made every piece
 * of map art an effect token: auto-attached as art on every link, with its own
 * controls disabled so the DM could not even deselect it. That is how a
 * battlemap kept riding along with a mount — not a stale link, a rule.
 *
 * A layer says where something was dropped, not what it is. Effect art is
 * recognised by the name we gave it when we placed it.
 */
export const isEffectToken = (t: { name: string; layer: string }) => EFFECT_WORDS.test(t.name || "") || AURAS.some((a) => a.match.test(t.name || ""));
/** The prone marker the DM named (any marker whose name holds it), default "prone". */
export const proneMarkerOf = (layout: Layout) => String(layout.tokenProneMarker || "prone");
export interface LaneToken { id: string; name: string; layer: string; left: number; top: number; width: number; height: number; imgsrc: string; group: string; linked?: string | null }
export interface LaneResult { ok: boolean; error?: string; page?: string; base?: string; tokens?: LaneToken[]; links?: TokenLink[]; events?: LaneEvent[]; seq?: number; installed?: number; cleared?: number }

/**
 * What may take part in a link at all: tokens on the TOKEN layer.
 *
 * ONE definition, used by the picker's rows, by the kinds it seeds, and by the
 * base it guesses. Having it in only some of those is what took a whole evening
 * to find: the rows were filtered while the model still carried the battlemap,
 * which hid the symptom in the interface and left the behaviour exactly as it
 * was. A map or GM-layer item is also a privacy matter — a map file name can say
 * more than the DM meant to share, and players reach these controls too.
 */
export const linkCandidates = <T extends { layer: string }>(tokens: T[] | null | undefined): T[] =>
  (tokens || []).filter((t) => t.layer === "objects");

/**
 * Which of the overlapping tokens is the thing the others ride on: the largest
 * footprint first, a named token before unnamed art, never an effect.
 *
 * It scores by FOOTPRINT, so on a page with a battlemap the map wins every time
 * — and a map that becomes the base has every rider grouped to it, so the whole
 * board slides when the creature moves. That was the bug. The map used to be
 * excluded as a side effect of isEffectToken treating everything off the token
 * layer as art; narrowing that (so a ship-sized creature on the map layer could
 * still be linked) quietly removed the only thing keeping the map out. The guard
 * is now the candidate rule, stated here so no caller can forget it.
 */
export function guessBase(tokens: LaneToken[]): string {
  const creatures = linkCandidates(tokens).filter((t) => !isEffectToken(t));
  const score = (t: LaneToken) => t.width * t.height * (t.name ? 1 : 0.5);
  return creatures.slice().sort((a, b) => score(b) - score(a))[0]?.id || "";
}

const KEY = "tokenLinks";
export const tokenLinksOf = (layout: Layout): TokenLink[] => { const all = layout[KEY] as Record<string, TokenLink[]> | undefined; const list = all?.[layout.activeCampaign]; return Array.isArray(list) ? list : []; };
export const withTokenLinks = (layout: Layout, links: TokenLink[]): Layout => ({ ...layout, [KEY]: { ...((layout[KEY] as Record<string, TokenLink[]>) || {}), [layout.activeCampaign]: links } });
export const tokenFollowOn = (layout: Layout) => layout.tokenFollow !== false;

/** Whose turn it is, by name, for the wording of a dismount on someone else's turn. */
export function currentTurnName(layout: Layout): string {
  const id = String(layout.activeTurnId || "");
  if (!id) return "";
  const pc = layout.characters.find((c) => c.id === id); if (pc) return pc.name;
  const ext = ((layout.externalInitiative || []) as { id: string; name?: string }[]).find((e) => e.id === id);
  return ext?.name || "";
}
const same = (a: string, b: string) => a.replace(/\s+/g, " ").trim().toLowerCase() === b.replace(/\s+/g, " ").trim().toLowerCase();
/** The activity line for a lane event: the DM's wording rules — a controlled
 *  mount mounts/dismounts, a creature is climbed onto / off; on another
 *  creature's turn the rider is dismounted BY that creature; a base falling
 *  prone throws whoever was aboard. */
export function laneEventLine(e: LaneEvent, turn: string): { text: string; meta: string } {
  const rider = e.rider.name || "A token", base = e.base.name || "its base";
  const ownTurn = !turn || same(turn, rider);
  if (e.kind === "thrown") return { text: e.mount ? `${base} falls prone, ${rider} is dismounted!` : `${base} falls prone, ${rider} is thrown off!`, meta: "the prone marker went on the base; drop the rider back on to re-mount" };
  if (e.kind === "mount") return { text: e.mount ? `${rider} mounts ${base}` : `${rider} climbs onto ${base}`, meta: ownTurn ? "dropped onto it on the map" : `on ${turn}'s turn` };
  if (ownTurn) return { text: e.mount ? `${rider} dismounts ${base}` : `${rider} climbs off ${base}`, meta: "dragged clear on the map; drop it back on to re-mount" };
  return { text: e.mount ? `${rider} is dismounted from ${base} by ${turn}` : `${rider} is thrown from ${base} by ${turn}`, meta: `moved clear of ${base} on ${turn}'s turn` };
}

/* WHICH LANE EVENTS THE TABLE SHOULD HEAR.

   Reported 2026-09-29: "mounting, linking, etc. worked amazing... but it didn't
   broadcast any 'dismounted'." The events were detected and written to the
   activity log, which only the DM sees -- and a rider being on foot again is the
   players' business, not the DM's.

   The first answer after this screen starts watching carries whatever the lane
   had already buffered, because it asks from sequence 0. Those mounts and
   dismounts happened before anybody here was looking, so announcing them would
   replay the session's history into the game's chat on every page load. They
   still reach the activity log -- the DM catching up is the point of that log. */
export const laneEventsToAnnounce = <T>(events: readonly T[] | null | undefined, watchedBefore: boolean): T[] =>
  watchedBefore ? [...(events || [])] : [];

/** Merge a link into the table. A rider rides one base (it leaves any other
 *  group it rode in); a base may itself ride elsewhere and keeps its own riders —
 *  that is how a familiar on a PC on a horse chains. Same base = replaced. */
/**
 * Add a link, replacing any the same base already had.
 *
 * Matched with sameToken, not by id. Matching the base by id alone meant that
 * re-linking a token the DM had REPLACED in Roll20 — new id, same name — appended
 * a second link for the same pair instead of replacing the first. They stack up
 * silently, and everything that reads links per-pair then does its work once per
 * copy: at the table that showed up as a swallowed player taking the recurring
 * bite ten times in one turn, and as a behir "holding more than its statblock
 * says" while holding one person.
 */
export function mergeLink(links: TokenLink[], link: TokenLink): TokenLink[] {
  const kept = links
    .filter((l) => !sameToken(l.base, link.base))
    .map((l) => ({ ...l, riders: l.riders.filter((r) => !link.riders.some((x) => sameToken(x, r))) }))
    .filter((l) => l.riders.length > 0);
  return [...kept, link];
}
/**
 * Is this stored reference the same token as this one on the map?
 *
 * By id when both have one, by NAME otherwise — and by name even when both have
 * ids that disagree, because a token replaced in Roll20 keeps its name and gets a
 * new id. That is not a rare case: it is what a DM does when a link misbehaves,
 * which means the id is least trustworthy exactly when it matters most.
 *
 * Matching on the id alone is how a link survived while its KIND did not. The
 * picker found the link by name and then looked the rider up by id, found
 * nothing, and showed "— not attached —" over a link that plainly existed. A
 * swallowed player therefore stopped being swallowed the moment the DM replaced
 * a token, silently, with the link still on screen saying otherwise.
 */
export const sameToken = (a: { id?: string; name?: string }, b: { id?: string; name?: string }): boolean => {
  const ida = a.id && a.id !== "-1" ? a.id : "";
  const idb = b.id && b.id !== "-1" ? b.id : "";
  if (ida && idb && ida === idb) return true;
  const na = String(a.name || "").replace(/\s+/g, " ").trim().toLowerCase();
  const nb = String(b.name || "").replace(/\s+/g, " ").trim().toLowerCase();
  return Boolean(na) && na === nb;
};

/**
 * Membership from the game tab, kinds from here.
 *
 * The connector's table says WHO rides WHAT — it reads Roll20 and is the
 * authority on that. It knows nothing about attachment kinds, so its riders
 * come back as {id, name, attached} with no kind at all, and taking its copy
 * wholesale wiped "swallowed" back to the default on the next poll — a few
 * seconds after every link.
 */
export function keepKinds(previous: TokenLink[], incoming: TokenLink): TokenLink {
  const was = previous.find((l) => l.base.id === incoming.base.id || (Boolean(l.base.name) && l.base.name === incoming.base.name));
  if (!was) return incoming;
  return {
    ...incoming,
    riders: incoming.riders.map((r) => {
      if (r.kind) return r;
      const before = was.riders.find((x) => sameToken(x, r));
      return before?.kind ? { ...r, kind: before.kind } : r;
    }),
  };
}

/** Riders currently aboard (not dismounted). */
export const aboard = (l: TokenLink) => l.riders.filter((r) => !r.off);
/** The players' own gestures, read off the game tab: a dismount leaves the
 *  group (an emptied group goes), a mount joins it. Matched by id, then name. */
export function applyLaneEvents(links: TokenLink[], events: LaneEvent[]): TokenLink[] {
  let out = links.map((l) => ({ ...l, riders: l.riders.slice() }));
  const same = (a: TokenRef, b: TokenRef) => a.id === b.id || (Boolean(a.name) && a.name === b.name);
  for (const e of events) {
    if (e.kind === "dismount" || e.kind === "thrown") out = out.map((l) => (same(l.base, e.base) ? { ...l, riders: l.riders.map((r) => (same(r, e.rider) ? { ...r, id: e.rider.id || r.id, off: true } : r)) } : l));
    else {
      const g = out.find((l) => same(l.base, e.base));
      if (g) { const had = g.riders.find((r) => same(r, e.rider)); if (had) { delete had.off; had.id = e.rider.id || had.id; } else g.riders.push({ id: e.rider.id, name: e.rider.name }); }
      else out.push({ base: e.base, riders: [{ id: e.rider.id, name: e.rider.name }] });
    }
  }
  return out;
}
/** Drop the group with that base; what its riders carry stays linked. */
export const dropLink = (links: TokenLink[], baseId: string) => links.filter((l) => l.base.id !== baseId);
/** Drop one rider (an effect token) from whatever group carries it; a group left empty goes. */
export const dropRider = (links: TokenLink[], riderId: string) => links.map((l) => ({ ...l, riders: l.riders.filter((r) => r.id !== riderId) })).filter((l) => l.riders.length > 0);
/** Drop every group holding any of the ids. */
export const dropLinks = (links: TokenLink[], ids: string[]) => links.filter((l) => !ids.includes(l.base.id) && !l.riders.some((r) => ids.includes(r.id)));

/** One lane call: the page half carries it to the game tab. */
export function tokenOp(op: Record<string, unknown>, timeoutMs = 15000): Promise<LaneResult> {
  return request<LaneResult>({ type: "DM_SCREEN_LMC_TOKENS_REQUEST", requestId: newRequestId("tokens"), op }, { resultType: "DM_SCREEN_LMC_TOKENS_RESULT", timeoutMs })
    .catch((e) => ({ ok: false, error: e?.message || String(e) }));
}

// The lane's readiness, announced by the page half on load and on every ping.
let laneReady = false;
const subs = new Set<() => void>();
let watching = false;
export function watchTokenLane() {
  if (watching) return;
  watching = true;
  onBridge((m) => {
    if (m.type === "DM_SCREEN_LMC_TOKENS_READY") { const was = laneReady; laneReady = Boolean((m.capabilities as Record<string, boolean> | undefined)?.tokenLinks); if (was !== laneReady) subs.forEach((fn) => fn()); }
  });
}
export const subscribeTokenLane = (fn: () => void) => { watchTokenLane(); subs.add(fn); return () => { subs.delete(fn); }; };
export const tokenLaneReady = () => laneReady;

